import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
  Lock,
  Loader2,
  Plus,
  Users,
  Pencil,
  Trash2,
  UserMinus,
  UserPlus,
  Search,
  X,
  Building2,
  Save,
} from "lucide-react";

export default function TeamsAdmin() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(null);

  // Create team
  const [createOpen, setCreateOpen] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createDescription, setCreateDescription] = useState("");
  const [creating, setCreating] = useState(false);

  // Edit team
  const [editOpen, setEditOpen] = useState(false);
  const [editTeam, setEditTeam] = useState(null);
  const [editName, setEditName] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  // Delete team
  const [deleteTeamDialog, setDeleteTeamDialog] = useState({ open: false, team: null });
  const [deletingTeam, setDeletingTeam] = useState(false);

  // Add members
  const [addMembersOpen, setAddMembersOpen] = useState(false);
  const [memberQuery, setMemberQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [pickedUserIds, setPickedUserIds] = useState([]);
  const [addingMembers, setAddingMembers] = useState(false);

  // Auto-add new users to a default team
  const [autoAddEnabled, setAutoAddEnabled] = useState(false);
  const [autoAddTeamId, setAutoAddTeamId] = useState("");
  const [autoAddDomain, setAutoAddDomain] = useState("");
  const [savingAutoAdd, setSavingAutoAdd] = useState(false);

  useEffect(() => {
    if (user?.is_admin) {
      fetchTeams();
      fetchAutoAdd();
    } else {
      setLoading(false);
    }
    // fetchTeams/fetchAutoAdd are stable helpers; re-run only when the user changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Debounced search
  useEffect(() => {
    if (!addMembersOpen) return;
    const handle = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await fetch(
          `${API}/admin/users/search?q=${encodeURIComponent(memberQuery)}`,
          { credentials: "include" }
        );
        if (res.ok) setSearchResults(await res.json());
      } catch (e) {
        console.warn("User search failed:", e);
      } finally {
        setSearchLoading(false);
      }
    }, 250);
    return () => clearTimeout(handle);
  }, [memberQuery, addMembersOpen]);

  const fetchTeams = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/teams`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setTeams(data);
        if (data.length > 0 && !selectedTeamId) {
          setSelectedTeamId(data[0].team_id);
        }
      } else {
        toast.error("Failed to load teams");
      }
    } catch {
      toast.error("Failed to load teams");
    } finally {
      setLoading(false);
    }
  };

  const selectedTeam = useMemo(
    () => teams.find((t) => t.team_id === selectedTeamId) || null,
    [teams, selectedTeamId]
  );

  const fetchAutoAdd = async () => {
    try {
      const res = await fetch(`${API}/admin/auto-add-team`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setAutoAddEnabled(!!data.enabled);
        setAutoAddTeamId(data.team_id || "");
        setAutoAddDomain(data.domain || "");
      }
    } catch {
      /* non-critical */
    }
  };

  const handleSaveAutoAdd = async () => {
    setSavingAutoAdd(true);
    try {
      const res = await fetch(`${API}/admin/auto-add-team`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          enabled: autoAddEnabled,
          team_id: autoAddTeamId || null,
          domain: autoAddDomain.trim() || null,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        setAutoAddEnabled(!!data.enabled);
        setAutoAddTeamId(data.team_id || "");
        setAutoAddDomain(data.domain || "");
        toast.success("Auto-add settings saved");
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to save settings");
      }
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSavingAutoAdd(false);
    }
  };

  const handleCreateTeam = async () => {
    if (!createName.trim()) {
      toast.error("Team name is required");
      return;
    }
    setCreating(true);
    try {
      const res = await fetch(`${API}/admin/teams`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: createName.trim(),
          description: createDescription.trim(),
          member_user_ids: [],
        }),
      });
      if (res.ok) {
        const team = await res.json();
        toast.success("Team created");
        setCreateOpen(false);
        setCreateName("");
        setCreateDescription("");
        await fetchTeams();
        setSelectedTeamId(team.team_id);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to create team");
      }
    } catch {
      toast.error("Failed to create team");
    } finally {
      setCreating(false);
    }
  };

  const openEditDialog = (team) => {
    setEditTeam(team);
    setEditName(team.name);
    setEditDescription(team.description || "");
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editName.trim() || !editTeam) return;
    setSavingEdit(true);
    try {
      const res = await fetch(`${API}/admin/teams/${editTeam.team_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: editName.trim(),
          description: editDescription.trim(),
        }),
      });
      if (res.ok) {
        toast.success("Team updated");
        setEditOpen(false);
        await fetchTeams();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to update team");
      }
    } catch {
      toast.error("Failed to update team");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleDeleteTeam = async () => {
    if (!deleteTeamDialog.team) return;
    setDeletingTeam(true);
    try {
      const res = await fetch(`${API}/admin/teams/${deleteTeamDialog.team.team_id}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (res.ok) {
        toast.success("Team deleted");
        setSelectedTeamId(null);
        await fetchTeams();
      } else {
        toast.error("Failed to delete team");
      }
    } catch {
      toast.error("Failed to delete team");
    } finally {
      setDeletingTeam(false);
      setDeleteTeamDialog({ open: false, team: null });
    }
  };

  const handleRemoveMember = async (userId) => {
    if (!selectedTeam) return;
    try {
      const res = await fetch(
        `${API}/admin/teams/${selectedTeam.team_id}/members/${userId}`,
        { method: "DELETE", credentials: "include" }
      );
      if (res.ok) {
        toast.success("Member removed");
        await fetchTeams();
      } else {
        toast.error("Failed to remove member");
      }
    } catch {
      toast.error("Failed to remove member");
    }
  };

  const handleAddMembers = async () => {
    if (!selectedTeam || pickedUserIds.length === 0) return;
    setAddingMembers(true);
    try {
      const res = await fetch(`${API}/admin/teams/${selectedTeam.team_id}/members`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ user_ids: pickedUserIds }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Added ${data.added} member(s)`);
        setAddMembersOpen(false);
        setPickedUserIds([]);
        setMemberQuery("");
        await fetchTeams();
      } else {
        toast.error("Failed to add members");
      }
    } catch {
      toast.error("Failed to add members");
    } finally {
      setAddingMembers(false);
    }
  };

  const togglePickedUser = (uid) => {
    setPickedUserIds((prev) =>
      prev.includes(uid) ? prev.filter((id) => id !== uid) : [...prev, uid]
    );
  };

  // Filter search results to exclude existing members
  const filteredSearchResults = useMemo(() => {
    const existingIds = new Set(selectedTeam?.member_user_ids || []);
    return searchResults.filter((u) => !existingIds.has(u.user_id));
  }, [searchResults, selectedTeam]);

  if (!user?.is_admin) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="teams-admin-page">
        <Card className="text-center">
          <CardHeader>
            <div className="mx-auto w-16 h-16 rounded-full bg-muted flex items-center justify-center mb-4">
              <Lock className="w-8 h-8 text-muted-foreground" />
            </div>
            <CardTitle className="text-2xl">Team Management</CardTitle>
            <CardDescription>Admin access is required to manage teams.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="teams-admin-page">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="teams-admin-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold">Teams</h1>
          <p className="text-muted-foreground">
            Create teams and assign users. Shared templates are visible across teammates regardless of email domain.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} data-testid="create-team-btn">
          <Plus className="w-4 h-4 mr-2" />
          New Team
        </Button>
      </div>

      {teams.length > 0 && (
        <Card data-testid="auto-add-team-card">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <UserPlus className="w-5 h-5" /> Auto-Assign New Users
            </CardTitle>
            <CardDescription>
              Automatically add new sign-ups from a specific email domain to a default team.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div>
                <p className="font-medium text-sm">Enable auto-assign</p>
                <p className="text-xs text-muted-foreground">
                  When on, matching new users join the selected team on first login.
                </p>
              </div>
              <Switch
                checked={autoAddEnabled}
                onCheckedChange={setAutoAddEnabled}
                data-testid="auto-add-enabled-switch"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Default team</Label>
                <Select value={autoAddTeamId} onValueChange={setAutoAddTeamId}>
                  <SelectTrigger data-testid="auto-add-team-select">
                    <SelectValue placeholder="Select a team" />
                  </SelectTrigger>
                  <SelectContent>
                    {teams.map((t) => (
                      <SelectItem key={t.team_id} value={t.team_id} data-testid={`auto-add-team-option-${t.team_id}`}>
                        {t.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="auto-add-domain">Email domain</Label>
                <Input
                  id="auto-add-domain"
                  placeholder="e.g., greatmarketingllc.com"
                  value={autoAddDomain}
                  onChange={(e) => setAutoAddDomain(e.target.value)}
                  data-testid="auto-add-domain-input"
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Button onClick={handleSaveAutoAdd} disabled={savingAutoAdd} data-testid="save-auto-add-btn">
                {savingAutoAdd ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Settings
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {teams.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center space-y-4">
            <div className="mx-auto w-16 h-16 rounded-full bg-muted flex items-center justify-center">
              <Building2 className="w-8 h-8 text-muted-foreground" />
            </div>
            <div>
              <p className="font-medium">No teams yet</p>
              <p className="text-sm text-muted-foreground">
                Create a team to group users together. Members of the same team can see each other's shared templates.
              </p>
            </div>
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create your first team
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Teams list */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="w-5 h-5" /> All Teams
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ScrollArea className="h-[480px]">
                <div className="space-y-2">
                  {teams.map((team) => (
                    <button
                      key={team.team_id}
                      onClick={() => setSelectedTeamId(team.team_id)}
                      data-testid={`team-list-item-${team.team_id}`}
                      className={`w-full text-left p-3 rounded-lg border transition-colors ${
                        selectedTeamId === team.team_id
                          ? "border-primary bg-primary/5"
                          : "border-border hover:bg-muted"
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <p className="font-medium truncate">{team.name}</p>
                        <Badge variant="secondary">{team.member_count}</Badge>
                      </div>
                      {team.description && (
                        <p className="text-xs text-muted-foreground truncate mt-1">
                          {team.description}
                        </p>
                      )}
                    </button>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>

          {/* Team detail */}
          <Card className="lg:col-span-2" data-testid="team-detail-card">
            {selectedTeam ? (
              <>
                <CardHeader>
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <CardTitle className="text-xl">{selectedTeam.name}</CardTitle>
                      {selectedTeam.description && (
                        <CardDescription className="mt-1">
                          {selectedTeam.description}
                        </CardDescription>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => openEditDialog(selectedTeam)}
                        data-testid="edit-team-btn"
                      >
                        <Pencil className="w-4 h-4 mr-2" /> Edit
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDeleteTeamDialog({ open: true, team: selectedTeam })}
                        data-testid="delete-team-btn"
                      >
                        <Trash2 className="w-4 h-4 mr-2 text-destructive" /> Delete
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-sm font-medium">
                      Members ({selectedTeam.member_count})
                    </h4>
                    <Button
                      size="sm"
                      onClick={() => {
                        setAddMembersOpen(true);
                        setMemberQuery("");
                        setPickedUserIds([]);
                      }}
                      data-testid="add-team-members-btn"
                    >
                      <UserPlus className="w-4 h-4 mr-2" /> Add Members
                    </Button>
                  </div>
                  <ScrollArea className="h-[380px] -mx-2 px-2">
                    {selectedTeam.members.length === 0 ? (
                      <div className="text-center py-12 text-sm text-muted-foreground">
                        No members yet. Click "Add Members" to assign users.
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {selectedTeam.members.map((m) => (
                          <div
                            key={m.user_id}
                            className="flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-muted transition-colors"
                            data-testid={`team-member-row-${m.user_id}`}
                          >
                            <Avatar className="w-9 h-9">
                              <AvatarImage src={m.picture} alt={m.name} />
                              <AvatarFallback>{m.name?.charAt(0)}</AvatarFallback>
                            </Avatar>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium truncate">{m.name}</p>
                              <p className="text-xs text-muted-foreground truncate">
                                {m.email}
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-destructive"
                              onClick={() => handleRemoveMember(m.user_id)}
                              data-testid={`remove-member-${m.user_id}`}
                            >
                              <UserMinus className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </>
            ) : (
              <CardContent className="py-12 text-center text-muted-foreground">
                Select a team to view its members
              </CardContent>
            )}
          </Card>
        </div>
      )}

      {/* Create Team Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Team</DialogTitle>
            <DialogDescription>
              Group users together so they share templates regardless of email domain.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="team-name">Team Name</Label>
              <Input
                id="team-name"
                placeholder="e.g., Sales East"
                value={createName}
                onChange={(e) => setCreateName(e.target.value)}
                data-testid="create-team-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="team-description">Description (optional)</Label>
              <Textarea
                id="team-description"
                placeholder="What is this team for?"
                value={createDescription}
                onChange={(e) => setCreateDescription(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateTeam} disabled={creating} data-testid="confirm-create-team-btn">
              {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
              Create Team
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Team Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Team</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-team-name">Team Name</Label>
              <Input
                id="edit-team-name"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-team-description">Description</Label>
              <Textarea
                id="edit-team-description"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={savingEdit}>
              {savingEdit ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Members Dialog */}
      <Dialog open={addMembersOpen} onOpenChange={setAddMembersOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Add Members to {selectedTeam?.name}</DialogTitle>
            <DialogDescription>
              Search across all users (any allowed domain) and pick who to add.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                value={memberQuery}
                onChange={(e) => setMemberQuery(e.target.value)}
                className="pl-9"
                data-testid="search-members-input"
              />
            </div>
            <ScrollArea className="h-[280px] border rounded-lg">
              {searchLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin" />
                </div>
              ) : filteredSearchResults.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  No users found
                </p>
              ) : (
                <div className="divide-y divide-border">
                  {filteredSearchResults.map((u) => {
                    const picked = pickedUserIds.includes(u.user_id);
                    return (
                      <button
                        key={u.user_id}
                        onClick={() => togglePickedUser(u.user_id)}
                        className={`w-full flex items-center gap-3 p-3 text-left hover:bg-muted transition-colors ${
                          picked ? "bg-primary/5" : ""
                        }`}
                        data-testid={`pick-user-${u.user_id}`}
                      >
                        <Avatar className="w-9 h-9">
                          <AvatarImage src={u.picture} alt={u.name} />
                          <AvatarFallback>{u.name?.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{u.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                        </div>
                        {picked && <Badge>Selected</Badge>}
                      </button>
                    );
                  })}
                </div>
              )}
            </ScrollArea>
            {pickedUserIds.length > 0 && (
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">{pickedUserIds.length} selected</span>
                <Button variant="ghost" size="sm" onClick={() => setPickedUserIds([])}>
                  <X className="w-3 h-3 mr-1" /> Clear
                </Button>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddMembersOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddMembers}
              disabled={pickedUserIds.length === 0 || addingMembers}
              data-testid="confirm-add-members-btn"
            >
              {addingMembers ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <UserPlus className="w-4 h-4 mr-2" />
              )}
              Add {pickedUserIds.length || ""} Member{pickedUserIds.length === 1 ? "" : "s"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Team Confirm */}
      <AlertDialog
        open={deleteTeamDialog.open}
        onOpenChange={(open) => !deletingTeam && setDeleteTeamDialog({ open, team: open ? deleteTeamDialog.team : null })}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete team "{deleteTeamDialog.team?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the team and unlinks its members. User accounts and their data are NOT deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingTeam}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTeam}
              disabled={deletingTeam}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-team-btn"
            >
              {deletingTeam ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Delete Team
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
